import { useEffect, useState, useRef } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { CommodityTicker, WeatherHUD, PinnedCommodityStrip, EliteBurstCanvas, CosmicStaticTouchLayer } from "@/components/dashboard/SpaceAgeControlDeck";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useCountUp } from "@/lib/useCountUp";
import {
  Receipt as ReceiptIcon,
  TrendingUp,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Calendar,
  ScanLine,
  Mail,
  Copy,
  Check,
  Send,
  Droplet,
  Zap,
  AlertTriangle,
  Lightbulb,
  X,
  CalendarCheck,
  Lock,
  Star,
  Flame,
  Eye,
  Download,
  FileText,
  ChevronDown,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { LoopholeTriageWidget } from "@/components/LoopholeTriageModal";
import { TaxBracketEscapeRing } from "@/components/dashboard/TaxBracketEscapeRing";
import { LiquidityRunwaySineWave } from "@/components/dashboard/LiquidityRunwaySineWave";
import { NeonReactorBurnMatrix } from "@/components/dashboard/NeonReactorBurnMatrix";
import {
  WriteOffPredictor,
  TaxSavingsMeter,
  PriorityQueueMirror,
  CashAdvanceMirror,
} from "@/components/dashboard/HeaderMirrors";
import { FinancialHealthScore } from "@/components/dashboard/FinancialHealthScore";
import { DeepStarfieldCanvas } from "@/components/dashboard/DeepStarfieldCanvas";
import {
  DeductionEfficiencyMeter,
  BurnRateOdometer,
  CpaReadinessCheck,
} from "@/components/dashboard/MiniMeters";
import { AuditRiskMeter, RiskExposureLog } from "@/components/dashboard/AuditRiskMeter";
import { PredictiveInsightsTray } from "@/components/dashboard/PredictiveInsights";
import { HolographicPrism } from "@/components/dashboard/HolographicPrism";
import { PerimeterShieldWrapper } from "@/components/dashboard/PerimeterShieldWrapper";
import { TreasuryPreview } from "@/components/dashboard/TreasuryPreview";
import { PremiumShieldGate } from "@/components/dashboard/PremiumShieldGate";
import { PaycheckOptimizerNode } from "@/components/dashboard/PaycheckOptimizerNode";
import { NebulaSettlementVault } from "@/components/dashboard/NebulaSettlementVault";
import { RetirementRunwayConsole } from "@/components/dashboard/RetirementRunwayConsole";
import { BackdoorRothMatrix } from "@/components/dashboard/BackdoorRothMatrix";
import { MileageDeductionOdometer } from "@/components/dashboard/MileageDeductionOdometer";
import { CAT_LABELS } from "@/lib/categoryLabels";

// ---------- First-Time Product Tour (3-step glassmorphic tooltip overlay) ----------
const TOUR_STEPS = [
  {
    id: "ingestion",
    title: "1 · The Magic Inbox",
    body: "Forward any receipt email or text a photo of a paper receipt to the address on your Ingestion tab — GPT-5.2 Vision OCRs it in seconds.",
    cta: "Show me Ingestion",
    highlightTestId: "nav-inbox",
    target: "/inbox",
  },
  {
    id: "gauges",
    title: "2 · Multi-Meter Analytics Cockpit",
    body: "Watch your Deduction Efficiency, Burn Rate, and CPA-Readiness in real time — plus the composite Financial Health Score above.",
    cta: "Got it",
    highlightTestId: "analytics-gauges-row",
    target: null,
  },
  {
    id: "cpa",
    title: "3 · 1-Click Schedule C Export",
    body: "Head to Receipts, pick a tax year, and hit Export Schedule C — email it straight to your accountant with one checkbox.",
    cta: "Open Receipts",
    highlightTestId: "nav-receipts",
    target: "/receipts",
  },
];

function PaycheckIngestBurst({ user }) {
  const tier = String(user?.subscription_tier || "").toLowerCase();
  const [pieces, setPieces] = useState([]);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (tier !== "premium_shield") return;
    const onIngest = () => {
      const GOLD = ["#FFD166", "#F7B733", "#FFE59A", "#F4C542", "#FFF3B0", "#E0AA3E"];
      const N = 32;
      const gen = Array.from({ length: N }).map((_, i) => {
        const angle = (Math.PI * 2 * i) / N + (Math.random() - 0.5) * 0.4;
        const dist = 140 + Math.random() * 120;
        return {
          id: `ingest-${Date.now()}-${i}`,
          color: GOLD[i % GOLD.length],
          dx: Math.cos(angle) * dist,
          dy: Math.sin(angle) * dist - Math.random() * 60,
          rot: Math.random() * 540 - 270,
          size: 5 + Math.random() * 6,
          delay: Math.random() * 120,
          duration: 1200 + Math.random() * 700,
        };
      });
      setPieces(gen);
      setTimeout(() => { if (isMountedRef.current) setPieces([]); }, 2400);
    };
    window.addEventListener("paycheck-stub-ingested", onIngest);
    return () => window.removeEventListener("paycheck-stub-ingested", onIngest);
  }, [tier]);

  if (pieces.length === 0) return null;
  return (
    <div
      data-testid="paycheck-ingest-burst"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[9650] flex items-center justify-center"
    >
      <style>{`
        @keyframes elite-gold-fly {
          0%   { transform: translate(0,0) rotate(0deg) scale(0.6); opacity: 0; }
          10%  { opacity: 1; }
          70%  { opacity: 1; }
          100% { transform: translate(var(--edx), var(--edy)) rotate(var(--erot)) scale(1); opacity: 0; }
        }
      `}</style>
      {pieces.map((p) => (
        <span
          key={p.id}
          data-testid={`paycheck-gold-piece-${p.id}`}
          className="absolute rounded-sm"
          style={{
            width: p.size,
            height: p.size,
            background: p.color,
            boxShadow: `0 0 8px ${p.color}, 0 0 16px rgba(255,209,102,0.35)`,
            "--edx": `${p.dx}px`,
            "--edy": `${p.dy}px`,
            "--erot": `${p.rot}deg`,
            animation: `elite-gold-fly ${p.duration}ms cubic-bezier(0.16,1,0.3,1) forwards`,
            animationDelay: `${p.delay}ms`,
          }}
        />
      ))}
    </div>
  );
}


function EliteLaunchConfetti({ user, setUser }) {
  const tier = String(user?.subscription_tier || "").toLowerCase();
  const isFirst = user?.is_first_login === true;
  const [pieces, setPieces] = useState([]);
  const firedRef = useRef(false);
  const isMountedRef = useRef(true);
  // Refs so effect deps stay minimal and setUser can't cancel the unmount timer.
  const userRef = useRef(user);
  const setUserRef = useRef(setUser);
  useEffect(() => { userRef.current = user; setUserRef.current = setUser; }, [user, setUser]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (firedRef.current) return;
    if (tier !== "premium_shield" || !isFirst) return;
    firedRef.current = true;
    const GOLD = ["#FFD166", "#F7B733", "#FFE59A", "#F4C542", "#FFF3B0", "#E0AA3E"];
    const N = 64;
    const generated = Array.from({ length: N }).map((_, i) => {
      const angle = (Math.PI * 2 * i) / N + (Math.random() - 0.5) * 0.3;
      const dist = 220 + Math.random() * 160;
      return {
        id: `elite-${i}`,
        color: GOLD[i % GOLD.length],
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist - Math.random() * 80,
        rot: Math.random() * 720 - 360,
        size: 6 + Math.random() * 8,
        delay: Math.random() * 180,
        duration: 1600 + Math.random() * 900,
      };
    });
    setPieces(generated);
    // Do NOT clearTimeout on effect cleanup — StrictMode dev double-invokes the
    // effect and would cancel the timer without rescheduling (firedRef guards
    // the re-run). Instead guard the state writes with isMountedRef.
    setTimeout(() => {
      if (!isMountedRef.current) return;
      setPieces([]);
      const u = userRef.current;
      const su = setUserRef.current;
      api.post("/tour/complete", {}).catch(() => {});
      if (su && u) su({ ...u, is_first_login: false });
    }, 3200);
  }, [tier, isFirst]);

  if (pieces.length === 0) return null;
  return (
    <div
      data-testid="elite-launch-confetti"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[9700] flex items-center justify-center"
    >
      <style>{`
        @keyframes elite-gold-fly {
          0%   { transform: translate(0,0) rotate(0deg) scale(0.6); opacity: 0; }
          10%  { opacity: 1; }
          70%  { opacity: 1; }
          100% { transform: translate(var(--edx), var(--edy)) rotate(var(--erot)) scale(1); opacity: 0; }
        }
        @keyframes elite-gold-ring {
          0%   { box-shadow: 0 0 0 0 rgba(255,209,102,0.55), 0 0 0 0 rgba(255,209,102,0.35); opacity: 1; }
          100% { box-shadow: 0 0 0 220px rgba(255,209,102,0), 0 0 0 340px rgba(255,209,102,0); opacity: 0; }
        }
      `}</style>
      <div
        data-testid="elite-launch-ring"
        className="absolute w-6 h-6 rounded-full"
        style={{
          background: "radial-gradient(circle, rgba(255,209,102,0.9), rgba(255,209,102,0) 70%)",
          animation: "elite-gold-ring 1400ms cubic-bezier(0.22,1,0.36,1) forwards",
        }}
      />
      {pieces.map((p) => (
        <span
          key={p.id}
          data-testid={`elite-gold-piece-${p.id}`}
          className="absolute rounded-sm"
          style={{
            width: p.size,
            height: p.size,
            background: p.color,
            boxShadow: `0 0 10px ${p.color}, 0 0 22px rgba(255,209,102,0.35)`,
            "--edx": `${p.dx}px`,
            "--edy": `${p.dy}px`,
            "--erot": `${p.rot}deg`,
            animation: `elite-gold-fly ${p.duration}ms cubic-bezier(0.16,1,0.3,1) forwards`,
            animationDelay: `${p.delay}ms`,
          }}
        />
      ))}
    </div>
  );
}


function ProductTour({ user, setUser }) {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const isPremium =
    user?.subscription_tier === "pro" || user?.subscription_tier === "business";

  useEffect(() => {
    if (isPremium && user?.is_first_login) {
      // Small delay so the tour appears after dashboard first-paint animations settle
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, [isPremium, user?.is_first_login]);

  const close = async (jumpTo = null) => {
    setVisible(false);
    try {
      await api.post("/tour/complete", {});
    } catch (_) { /* silent */ }
    if (setUser && user) setUser({ ...user, is_first_login: false });
    if (jumpTo) navigate(jumpTo);
  };

  const next = () => {
    const current = TOUR_STEPS[step];
    if (step >= TOUR_STEPS.length - 1) {
      close(current.target);
      return;
    }
    setStep(step + 1);
  };

  if (!visible) return null;
  const s = TOUR_STEPS[step];

  return (
    <div
      data-testid="product-tour-overlay"
      className="fixed inset-0 z-[9500] flex items-center justify-center px-4"
      style={{ background: "rgba(2,6,23,0.72)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
    >
      <motion.div
        key={s.id}
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -16, scale: 0.96 }}
        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
        data-testid={`product-tour-step-${s.id}`}
        className="glass rounded-2xl w-full max-w-md p-6 relative overflow-hidden"
        style={{
          background: "linear-gradient(180deg, rgba(15,23,42,0.40) 0%, rgba(2,6,23,0.40) 100%)",
          border: "1px solid rgba(16,185,129,0.4)",
          boxShadow: "0 30px 70px -20px rgba(16,185,129,0.45)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
      >
        <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(16,185,129,0.3), transparent 60%)", filter: "blur(20px)" }} />
        <button
          onClick={() => close(null)}
          data-testid="product-tour-skip"
          className="absolute top-3 right-3 text-slate-400 hover:text-white p-1 rounded"
          aria-label="Skip tour"
        >
          <X size={16} />
        </button>
        <div className="relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-400 mb-2 flex items-center gap-1.5">
            <Sparkles size={11} /> Welcome tour · {step + 1} of {TOUR_STEPS.length}
          </div>
          <div className="text-white font-bold text-xl tracking-tight mb-2">{s.title}</div>
          <div className="text-slate-300 text-sm leading-relaxed mb-5">{s.body}</div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5">
              {TOUR_STEPS.map((_, i) => (
                <div key={i}
                     className="h-1.5 rounded-full transition-all"
                     style={{
                       width: i === step ? 22 : 6,
                       background: i <= step ? "#10B981" : "rgba(148,163,184,0.28)",
                     }} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => close(null)}
                data-testid="product-tour-dismiss"
                className="text-slate-400 hover:text-white text-xs font-mono uppercase tracking-widest px-2 py-1"
              >
                Skip
              </button>
              <button
                onClick={next}
                data-testid="product-tour-next"
                className="btn-neon px-4 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-sm font-semibold flex items-center gap-1.5"
              >
                {s.cta}
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}


function Stat({ label, value, sub, icon: Icon, accent, loading }) {
  const numeric = typeof value === "number" ? value : Number(String(value || "").replace(/[^0-9.\\-]/g, "")) || 0;
  const rolled = useCountUp(loading ? 0 : numeric, 900);
  return (
    <div className="glass rounded-2xl p-6 card-hover">
      <div className="flex items-center justify-between mb-4">
        <div className="text-slate-400 text-xs uppercase tracking-[0.2em] font-medium">
          {label}
        </div>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
          accent ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-slate-300"
        }`}>
          <Icon size={18} />
        </div>
      </div>
      {loading ? (
        <>
          <div className="skeleton h-8 w-32 mb-2" />
          <div className="skeleton h-3 w-24" />
        </>
      ) : (
        <>
          <div className={`font-mono font-bold tracking-tight text-3xl ${
            accent ? "text-emerald-400" : "text-white"
          }`}>
            ${rolled.toFixed(2)}
          </div>
          {sub && <div className="mt-2 text-slate-400 text-xs">{sub}</div>}
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [leaks, setLeaks] = useState([]);
  const [copied, setCopied] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [healthPulse, setHealthPulse] = useState(false);
  const [capitalAdvance, setCapitalAdvance] = useState(25000);
  const [factorRate, setFactorRate] = useState(1.11);
  const [advanceHistory, setAdvanceHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [digestPreviewOpen, setDigestPreviewOpen] = useState(false);
  const [digestPreviewHtml, setDigestPreviewHtml] = useState(null);
  const [digestPreviewLoading, setDigestPreviewLoading] = useState(false);
  const [accountantEmail, setAccountantEmail] = useState("");
  const [sendingSheet, setSendingSheet] = useState(false);
  const [sheetSentId, setSheetSentId] = useState(null);
  const [pinnedScenarios, setPinnedScenarios] = useState([]);
  const [digestInterval, setDigestInterval] = useState(168);
  const [settingInterval, setSettingInterval] = useState(false);

  const downloadTermSheet = (entry) => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210;
    const H = 297;

    // White background
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, W, H, "F");

    // Violet header strip
    doc.setFillColor(109, 40, 217);
    doc.rect(0, 0, W, 8, "F");

    // Accent line
    doc.setFillColor(167, 139, 250);
    doc.rect(0, 8, W, 1.5, "F");

    // Brand
    doc.setFontSize(18);
    doc.setTextColor(15, 10, 40);
    doc.setFont("helvetica", "bold");
    doc.text("RECEIPT SHEETS CAPITAL", 20, 24);

    doc.setFontSize(9);
    doc.setTextColor(109, 40, 217);
    doc.setFont("helvetica", "normal");
    doc.text("MERCHANT CASH ADVANCE  ·  SANDBOX TERM SHEET", 20, 31);

    // PRE-APPROVED badge
    doc.setDrawColor(245, 158, 11);
    doc.setLineWidth(1.2);
    doc.roundedRect(145, 14, 48, 18, 3, 3, "S");
    doc.setFontSize(9.5);
    doc.setTextColor(180, 100, 0);
    doc.setFont("helvetica", "bold");
    doc.text("PRE-APPROVED", 169, 22, { align: "center" });
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "normal");
    doc.text("BETA SANDBOX", 169, 28, { align: "center" });

    // Divider
    doc.setDrawColor(200, 200, 220);
    doc.setLineWidth(0.3);
    doc.line(20, 38, W - 20, 38);

    // Fields
    const costPct = ((entry.factorRate - 1) * 100).toFixed(0);
    const fields = [
      ["Capital Advance Amount",    `$${entry.advance.toLocaleString()}`],
      ["Factor Rate",               `${entry.factorRate.toFixed(2)}×  (${costPct}% total cost of advance)`],
      ["Total Repayment",           `$${entry.totalRepayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
      ["Est. Daily Remittance",     `$${entry.dailyRemittance.toFixed(2)} / day  (avg · 180-day window)`],
      ["Remittance Method",         "Revenue-based · auto-debited from daily card volume"],
      ["Payment Schedule",          "No fixed monthly payment required"],
      ["Credit Check",              "None required"],
      ["Effective Term Window",     "180 days (estimated)"],
      ["Scenario Recorded At",      entry.ts],
      ["Approval Reference ID",     `SANDBOX-${entry.id}`],
    ];

    let y = 50;
    fields.forEach(([label, value]) => {
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 150);
      doc.setFont("helvetica", "normal");
      doc.text(label.toUpperCase(), 20, y);

      doc.setFontSize(11);
      doc.setTextColor(15, 10, 40);
      doc.setFont("helvetica", "bold");
      doc.text(value, 20, y + 6);

      doc.setDrawColor(230, 228, 245);
      doc.setLineWidth(0.2);
      doc.line(20, y + 10, W - 20, y + 10);

      y += 16;
    });

    // Footer
    doc.setFillColor(248, 247, 255);
    doc.rect(0, H - 28, W, 28, "F");
    doc.setDrawColor(200, 200, 220);
    doc.setLineWidth(0.2);
    doc.line(20, H - 28, W - 20, H - 28);

    doc.setFontSize(7);
    doc.setTextColor(130, 130, 160);
    doc.setFont("helvetica", "normal");
    doc.text(
      "SANDBOX ONLY — This document is for illustrative purposes and does not constitute a binding financial offer.",
      20, H - 18
    );
    doc.text(
      "Receipt Sheets Capital (Beta) · receiptsheets.com · No live funds will be advanced.",
      20, H - 12
    );
    doc.setFontSize(7.5);
    doc.setTextColor(109, 40, 217);
    doc.setFont("helvetica", "bold");
    doc.text("receiptsheets.com", W - 20, H - 12, { align: "right" });

    doc.save(`RS-Capital-${entry.id}.pdf`);
  };

  const downloadComparePDF = () => {
    if (pinnedScenarios.length === 0) return;
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const W = 210;

    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, W, 297, "F");
    doc.setFillColor(109, 40, 217);
    doc.rect(0, 0, W, 8, "F");
    doc.setFillColor(167, 139, 250);
    doc.rect(0, 8, W, 1.5, "F");

    doc.setFontSize(17);
    doc.setTextColor(15, 10, 40);
    doc.setFont("helvetica", "bold");
    doc.text("RECEIPT SHEETS CAPITAL", 20, 22);
    doc.setFontSize(9);
    doc.setTextColor(109, 40, 217);
    doc.setFont("helvetica", "normal");
    doc.text("MULTI-SCENARIO COMPARISON — SANDBOX TERM SHEET", 20, 29);
    doc.setFontSize(7.5);
    doc.setTextColor(150, 150, 180);
    doc.text(`Generated ${new Date().toLocaleString()} · ${pinnedScenarios.length} scenario${pinnedScenarios.length > 1 ? "s" : ""}`, 20, 35);

    const minRep = Math.min(...pinnedScenarios.map((e) => e.totalRepayment));
    const bestIdx = pinnedScenarios.findIndex((e) => e.totalRepayment === minRep);

    const rows = [
      ["Advance Amount",   ...pinnedScenarios.map((e) => `$${e.advance.toLocaleString()}`)],
      ["Factor Rate",      ...pinnedScenarios.map((e) => `${e.factorRate.toFixed(2)}× (${((e.factorRate - 1) * 100).toFixed(0)}% cost)`)],
      ["Total Repayment",  ...pinnedScenarios.map((e) => `$${e.totalRepayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)],
      ["Est. Daily Remit", ...pinnedScenarios.map((e) => `$${e.dailyRemittance.toFixed(2)}/day`)],
      ["Term Window",      ...pinnedScenarios.map(() => "180 days (est.)")],
      ["Payment Type",     ...pinnedScenarios.map(() => "Revenue-based")],
      ["Scenario ID",      ...pinnedScenarios.map((e) => `SANDBOX-${e.id}`)],
      ["Recorded",         ...pinnedScenarios.map((e) => e.ts)],
    ];

    autoTable(doc, {
      startY: 42,
      head: [["Metric", ...pinnedScenarios.map((_, i) => `Scenario ${i + 1}`)]],
      body: rows,
      theme: "grid",
      headStyles: { fillColor: [109, 40, 217], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9, halign: "center" },
      styles: { fontSize: 9, cellPadding: 3.5 },
      columnStyles: { 0: { fillColor: [248, 247, 255], textColor: [109, 40, 217], fontStyle: "bold", cellWidth: 42 } },
      didParseCell: (data) => {
        if (data.section === "body" && data.row.index === 2 && data.column.index > 0) {
          const sc = pinnedScenarios[data.column.index - 1];
          if (sc && sc.totalRepayment === minRep && pinnedScenarios.length > 1) {
            data.cell.styles.fillColor = [209, 250, 229];
            data.cell.styles.textColor = [6, 95, 70];
            data.cell.styles.fontStyle = "bold";
          }
        }
        if (data.section === "head" && data.column.index === bestIdx + 1 && pinnedScenarios.length > 1) {
          data.cell.styles.fillColor = [16, 185, 129];
        }
      },
    });

    if (pinnedScenarios.length > 1) {
      const finalY = (doc.lastAutoTable?.finalY ?? 120) + 8;
      doc.setFillColor(209, 250, 229);
      doc.roundedRect(20, finalY, W - 40, 12, 2, 2, "F");
      doc.setFontSize(8);
      doc.setTextColor(6, 95, 70);
      doc.setFont("helvetica", "bold");
      doc.text(
        `✓ BEST TERMS: Scenario ${bestIdx + 1} — $${pinnedScenarios[bestIdx].totalRepayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total repayment`,
        W / 2, finalY + 7.5, { align: "center" }
      );
    }

    doc.setFillColor(248, 247, 255);
    doc.rect(0, 297 - 22, W, 22, "F");
    doc.setDrawColor(200, 200, 220);
    doc.setLineWidth(0.2);
    doc.line(20, 297 - 22, W - 20, 297 - 22);
    doc.setFontSize(7);
    doc.setTextColor(130, 130, 160);
    doc.setFont("helvetica", "normal");
    doc.text("SANDBOX ONLY — For illustrative purposes. Does not constitute a binding financial offer.", 20, 297 - 14);
    doc.text("Receipt Sheets Capital (Beta) · receiptsheets.com · No live funds will be advanced.", 20, 297 - 8);
    doc.setFontSize(7.5);
    doc.setTextColor(109, 40, 217);
    doc.setFont("helvetica", "bold");
    doc.text("receiptsheets.com", W - 20, 297 - 8, { align: "right" });

    doc.save(`RS-Compare-${pinnedScenarios.length}sc-${Date.now()}.pdf`);
  };

  const loadDigestPreview = async () => {
    if (digestPreviewHtml) {
      setDigestPreviewOpen((o) => !o);
      return;
    }
    setDigestPreviewLoading(true);
    setDigestPreviewOpen(true);
    try {
      const r = await api.post("/digest/preview");
      setDigestPreviewHtml(r.data.html);
    } catch {
      setDigestPreviewHtml("<p style='color:#94a3b8;font-family:monospace;padding:24px'>Preview unavailable — check Brevo/Resend config.</p>");
    } finally {
      setDigestPreviewLoading(false);
    }
  };

  const sendTermSheetEmail = async (entry) => {
    if (!accountantEmail || !accountantEmail.includes("@")) {
      toast.error("Enter a valid email address first");
      return;
    }
    setSendingSheet(true);
    try {
      const r = await api.post("/v1/capital/send-term-sheet", { email: accountantEmail, scenario: entry });
      if (r.data.sent || r.data.simulated) {
        toast.success(`Term sheet sent to ${accountantEmail}`);
        setSheetSentId(entry.id);
      } else {
        toast.error("Send failed — check Resend API config");
      }
    } catch {
      toast.error("Send failed");
    } finally {
      setSendingSheet(false);
    }
  };

  const pinScenario = (entry) => {
    setPinnedScenarios((prev) => {
      const already = prev.some((p) => p.id === entry.id);
      if (already) return prev.filter((p) => p.id !== entry.id);
      if (prev.length >= 3) { toast.error("Max 3 scenarios pinned for comparison"); return prev; }
      return [...prev, entry];
    });
  };

  const updateDigestInterval = async (hours) => {
    setSettingInterval(true);
    try {
      await api.post("/v1/digest/set-interval", { hours: Number(hours) });
      setDigestInterval(Number(hours));
      const label = Number(hours) === 168 ? "Weekly" : Number(hours) === 336 ? "Bi-Weekly" : "Monthly";
      toast.success(`Digest cadence set to ${label}`);
    } catch {
      toast.error("Failed to update interval");
    } finally {
      setSettingInterval(false);
    }
  };
  const navigate = useNavigate();

  const alias =
    user?.forwarding_alias ||
    `${(user?.email || "you").split("@")[0]}@receiptsheets.com`;

  const copyAlias = () => {
    navigator.clipboard.writeText(alias);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/digest/send-me");
      const s = r.data?.summary || {};
      if (r.data?.simulated) {
        toast.success(`Digest queued (simulated) · ${s.leak_count ?? 0} leaks · ${s.user_deductible_pct ?? 0}% vs peers ${s.peer_deductible_pct ?? 0}%`);
      } else if (r.data?.sent) {
        toast.success(`Digest sent to your inbox · ${s.leak_count ?? 0} leaks flagged`);
      } else {
        toast.error(r.data?.error || "Digest send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send digest");
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => {
    // Track consecutive silent poll failures across the 3 primary Dashboard endpoints.
    // At the 3rd consecutive fail we surface a subtle "connection sluggish" pill so
    // users know the data is stale but the UI is still functional.  The pill dismisses
    // automatically on the next successful cycle.
    let consecutiveFails = 0;
    let sluggishToastId = null;
    const onFailCycle = () => {
      consecutiveFails += 1;
      if (consecutiveFails >= 3 && !sluggishToastId) {
        sluggishToastId = toast.warning("Connection sluggish", {
          description: "Live data is temporarily paused. We'll retry silently.",
          duration: Infinity,
          id: "dash-sluggish",
        });
      }
    };
    const onSuccessCycle = () => {
      if (consecutiveFails >= 3 && sluggishToastId) {
        toast.dismiss(sluggishToastId);
        sluggishToastId = null;
        toast.success("Connection restored", { duration: 2500 });
      }
      consecutiveFails = 0;
    };

    const load = async () => {
      // Fire the 3 primary polls in parallel and treat the CYCLE as failed if all
      // three reject (any one success = healthy connection).
      const results = await Promise.allSettled([
        api.get("/stats/summary").then((r) => setStats(r.data)),
        api.get("/receipts").then((r) => setRecent((r.data || []).slice(0, 5))),
        api.get("/insights/leaks").then((r) => setLeaks(r.data || [])),
      ]);
      const anyOk = results.some((r) => r.status === "fulfilled");
      if (anyOk) onSuccessCycle();
      else onFailCycle();
    };
    load();
    // Poll every 10s so newly-imported receipts (email/SMS/e-commerce webhook) surface
    // near-instantly — the Neon Reactor's ID-diff detector fires its cyan shockwave
    // exactly when a fresh receipt ID appears.  Pause when the tab is hidden.
    let iv = null;
    const start = () => { if (!iv) iv = setInterval(load, 10000); };
    const stop = () => { if (iv) { clearInterval(iv); iv = null; } };
    start();
    const onVis = () => (document.visibilityState === "visible" ? (load(), start()) : stop());
    document.addEventListener("visibilitychange", onVis);
    // Refresh on voice-log or any custom receipt-mutation event
    const onRefresh = () => load();
    window.addEventListener("receipts-refresh", onRefresh);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("receipts-refresh", onRefresh);
      if (sluggishToastId) toast.dismiss(sluggishToastId);
    };
  }, []);

  const fmt = (v) => `$${Number(v || 0).toFixed(2)}`;

  const topCategories = stats
    ? Object.entries(stats.by_category || {})
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
    : [];
  const maxCatVal = topCategories.length ? topCategories[0][1] : 1;

  return (
    <DashboardLayout>
      {/* Deep starfield — always-on twinkling stars + faint constellation web + continuous shooting stars */}
      <DeepStarfieldCanvas />
      {/* Full-viewport elite burst overlay — erupts from live card rect */}
      <EliteBurstCanvas />
      {/* Mobile touch-driven static electricity trail — passive listeners, pointer-events:none */}
      <CosmicStaticTouchLayer />
      <EliteLaunchConfetti user={user} setUser={setUser} />
      <PaycheckIngestBurst user={user} />
      <ProductTour user={user} setUser={setUser} />
      <LoopholeTriageWidget recent={recent} />
      {user?.space_age_deck_enabled && (
        <div className="mb-6" data-testid="space-age-deck-wrapper">
          <WeatherHUD />
          <div className="mt-3">
            <PinnedCommodityStrip />
          </div>
        </div>
      )}
      <div className="fade-up">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-emerald-400 font-mono text-xs uppercase tracking-[0.3em] mb-2">
              Overview
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter text-white">
              Good to see you, {user?.name?.split(" ")[0] || "there"}.
            </h1>
            <p className="text-slate-400 mt-2 text-sm">
              Every dollar accounted for. Every deduction flagged.
            </p>
          </div>
          <button
            onClick={() => navigate("/upload")}
            data-testid="dash-scan-btn"
            className="btn-neon h-12 px-5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold flex items-center gap-2"
          >
            <ScanLine size={16} /> Scan a receipt
          </button>
        </div>

        <PriorityQueueMirror user={user} healthPulse={healthPulse} />
        <CashAdvanceMirror user={user} receipts={recent} />
        <TaxSavingsMeter />

        <FinancialHealthScore stats={stats} receipts={recent} onPulse={setHealthPulse} />

        <div className="mb-6" id="audit-shield-row">
          <PremiumShieldGate
            label="AI Audit-Shield · Live Risk Score"
            tagline="Real-time IRS audit-risk grading powered by your live receipt ledger. Only Premium Shield subscribers see the live composite score & discrepancy alerts."
          >
            <AuditRiskMeter stats={stats} receipts={recent} user={user} />
          </PremiumShieldGate>
          <RiskExposureLog />
        </div>

        {/* Tax-Bracket Escape Ring + Liquidity Runway Wave — animated tandem */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="advanced-visuals-row">
          <TaxBracketEscapeRing stats={stats} />
          <LiquidityRunwaySineWave stats={stats} receipts={recent} />
        </div>

        {/* Neon Reactor + Holographic Prism — high-luminance space-age panel */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="reactor-prism-row">
          <NeonReactorBurnMatrix stats={stats} receipts={recent} />
          <HolographicPrism stats={stats} />
        </div>

        {/* Deduction · Burn Rate · CPA Readiness — synchronized fade-up + count-up on mount */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="analytics-gauges-row">
          <DeductionEfficiencyMeter stats={stats} />
          <BurnRateOdometer stats={stats} receipts={recent} />
          <CpaReadinessCheck receipts={recent} />
        </div>

        {/* 2027 Autonomous Treasury Preview — marketing waitlist entry, purely presentational */}
        <div className="mb-6" data-testid="treasury-preview-row">
          <TreasuryPreview />
        </div>

        {/* Premium Tax Shield — Paycheck Optimizer Node (live paystub ingestion + forecast) */}
        <div className="mb-6" id="paycheck-optimizer-node" data-testid="paycheck-forecast-row">
          <PremiumShieldGate
            label="Paycheck Optimizer · Live Trajectory"
            tagline="Upload a paystub — we OCR gross pay, withholding, and YTD balances, then chart your withholding drift so April never surprises you."
          >
            <PaycheckOptimizerNode />
          </PremiumShieldGate>
        </div>

        {/* Premium Tax Shield — 🛸 Nebula Inbound Settlement Vault */}
        <div className="mb-6" id="nebula-settlement-vault" data-testid="nebula-settlement-row">
          <PremiumShieldGate
            label="🛸 Nebula Inbound Settlement Vault"
            tagline="Stripe Connect-powered client payment links · 1% platform skim · cascades net into Cash Flash statutory reserves."
          >
            <NebulaSettlementVault />
          </PremiumShieldGate>
        </div>

        {/* Receipt Sheets Capital (Beta) — Merchant Cash Advance Sandbox */}
        {(() => {
          const totalRepayment = capitalAdvance * factorRate;
          const dailyRemittance = totalRepayment / 180;
          const pct = ((capitalAdvance - 5000) / (50000 - 5000)) * 100;
          const frPct = ((factorRate - 1.09) / (1.35 - 1.09)) * 100;
          const costPct = ((factorRate - 1) * 100).toFixed(0);
          return (
            <div className="mb-6" id="capital-advance-node" data-testid="capital-advance-row">
              <div
                className="rounded-2xl p-6 relative overflow-hidden"
                style={{
                  background: "linear-gradient(145deg, rgba(15,10,40,0.40) 0%, rgba(8,4,28,0.40) 100%)",
                  border: "1px solid rgba(139,92,246,0.35)",
                  boxShadow: "0 0 42px -12px rgba(139,92,246,0.35), inset 0 1px 0 rgba(139,92,246,0.15)",
                  backdropFilter: "blur(18px)",
                  WebkitBackdropFilter: "blur(18px)",
                }}
                data-testid="capital-advance-card"
              >
                {/* Background aurora glow */}
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full pointer-events-none"
                  style={{ background: "radial-gradient(circle, rgba(139,92,246,0.22), transparent 65%)", filter: "blur(28px)" }} />
                <div className="absolute -bottom-16 -left-16 w-52 h-52 rounded-full pointer-events-none"
                  style={{ background: "radial-gradient(circle, rgba(88,28,220,0.18), transparent 65%)", filter: "blur(22px)" }} />

                <div className="relative">
                  {/* Header row */}
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-violet-300">
                          Receipt Sheets Capital
                        </div>
                        <span
                          className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                          style={{ background: "rgba(139,92,246,0.22)", color: "#c4b5fd", border: "1px solid rgba(139,92,246,0.4)" }}
                          data-testid="capital-advance-beta-badge"
                        >
                          BETA
                        </span>
                      </div>
                      <h2 className="text-lg font-bold text-white tracking-tight">
                        Merchant Capital Advance
                      </h2>
                      <p className="text-slate-400 text-xs mt-1">
                        Revenue-based financing · no fixed monthly payment · daily remittance from card volume.
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                        style={{
                          background: "linear-gradient(90deg, rgba(245,158,11,0.22), rgba(251,191,36,0.18))",
                          border: "1px solid rgba(245,158,11,0.5)",
                          boxShadow: "0 0 14px -4px rgba(245,158,11,0.4)",
                        }}
                        data-testid="capital-advance-preapproved-badge"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300 font-semibold">
                          PRE-APPROVED
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Slider — Capital Amount */}
                  <div className="mb-4">
                    <div className="flex items-baseline justify-between mb-3">
                      <span className="text-sm text-slate-300 font-medium">Capital Request Amount</span>
                      <span
                        className="text-2xl font-mono font-bold text-white tracking-tight"
                        data-testid="capital-advance-amount"
                      >
                        ${capitalAdvance.toLocaleString()}
                      </span>
                    </div>
                    <div className="relative py-2">
                      <div
                        className="absolute h-1.5 rounded-full top-1/2 -translate-y-1/2 left-0 pointer-events-none"
                        style={{
                          width: `${pct}%`,
                          background: "linear-gradient(90deg, #7c3aed, #a78bfa)",
                          boxShadow: "0 0 10px rgba(139,92,246,0.6)",
                        }}
                      />
                      <input
                        type="range"
                        min={5000}
                        max={50000}
                        step={1000}
                        value={capitalAdvance}
                        onChange={(e) => setCapitalAdvance(Number(e.target.value))}
                        data-testid="capital-advance-slider"
                        className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: "rgba(139,92,246,0.15)",
                          outline: "none",
                          accentColor: "#a78bfa",
                        }}
                      />
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[10px] text-slate-500 font-mono">$5,000</span>
                      <span className="text-[10px] text-slate-500 font-mono">$50,000</span>
                    </div>
                  </div>

                  {/* Slider — Factor Rate Terms Editor (1.09×–1.35×) */}
                  <div className="mb-6 p-3 rounded-xl" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.18)" }}>
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-xs text-slate-400 font-medium">Factor Rate Terms Editor</span>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-xl font-mono font-bold text-violet-200 tracking-tight"
                          data-testid="capital-advance-factor-display"
                        >
                          {factorRate.toFixed(2)}×
                        </span>
                        <span
                          className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                          style={{ background: "rgba(139,92,246,0.2)", color: "#c4b5fd", border: "1px solid rgba(139,92,246,0.35)" }}
                        >
                          {costPct}% COST
                        </span>
                      </div>
                    </div>
                    <div className="relative py-2">
                      <div
                        className="absolute h-1 rounded-full top-1/2 -translate-y-1/2 left-0 pointer-events-none"
                        style={{
                          width: `${frPct}%`,
                          background: "linear-gradient(90deg, #6d28d9, #c4b5fd)",
                          boxShadow: "0 0 8px rgba(139,92,246,0.5)",
                        }}
                      />
                      <input
                        type="range"
                        min={1.09}
                        max={1.35}
                        step={0.01}
                        value={factorRate}
                        onChange={(e) => setFactorRate(Number(e.target.value))}
                        data-testid="capital-advance-factor-slider"
                        className="w-full h-1 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: "rgba(139,92,246,0.12)",
                          outline: "none",
                          accentColor: "#c4b5fd",
                        }}
                      />
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[10px] text-slate-500 font-mono">1.09× (9%)</span>
                      <span className="text-[10px] text-slate-500 font-mono">conservative</span>
                      <span className="text-[10px] text-slate-500 font-mono">aggressive</span>
                      <span className="text-[10px] text-slate-500 font-mono">1.35× (35%)</span>
                    </div>
                  </div>

                  {/* Factor breakdown grid */}
                  <div
                    className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 rounded-xl mb-5"
                    style={{
                      background: "rgba(139,92,246,0.08)",
                      border: "1px solid rgba(139,92,246,0.2)",
                    }}
                    data-testid="capital-advance-breakdown"
                  >
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono mb-1">Factor Rate</div>
                      <div className="text-lg font-mono font-bold text-violet-300" data-testid="capital-advance-factor-rate">{factorRate.toFixed(2)}×</div>
                      <div className="text-[10px] text-slate-500 font-mono">{costPct}% total cost</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono mb-1">Total Payback</div>
                      <div className="text-lg font-mono font-bold text-white" data-testid="capital-advance-total-repayment">
                        ${totalRepayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono">principal + factor</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono mb-1">Est. Daily Remit</div>
                      <div className="text-lg font-mono font-bold text-emerald-300" data-testid="capital-advance-daily-remittance">
                        ${dailyRemittance.toFixed(2)}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono">avg · 180-day window</div>
                    </div>
                  </div>

                  {/* Terms strip */}
                  <div
                    className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 rounded-xl"
                    style={{ background: "rgba(15,10,40,0.6)", border: "1px solid rgba(139,92,246,0.12)" }}
                  >
                    <span className="text-[10px] text-slate-500 font-mono">Remittance Rate: <span className="text-violet-300">{costPct}% of daily card volume</span></span>
                    <span className="text-[10px] text-slate-500 font-mono">No fixed payment schedule</span>
                    <span className="text-[10px] text-slate-500 font-mono">No credit check required</span>
                    <span
                      className="ml-auto text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded"
                      style={{ background: "rgba(139,92,246,0.15)", color: "#a78bfa", border: "1px solid rgba(139,92,246,0.3)" }}
                    >
                      SANDBOX — NO LIVE FUNDS
                    </span>
                  </div>

                  {/* Record Scenario + History Log */}
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      data-testid="capital-advance-record-btn"
                      onClick={() => {
                        const entry = {
                          id: Date.now(),
                          advance: capitalAdvance,
                          factorRate,
                          totalRepayment,
                          dailyRemittance,
                          ts: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
                        };
                        setAdvanceHistory((prev) => [entry, ...prev].slice(0, 12));
                        setHistoryOpen(true);
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest transition-all hover:brightness-110 active:scale-95"
                      style={{ background: "rgba(139,92,246,0.18)", border: "1px solid rgba(139,92,246,0.4)", color: "#c4b5fd" }}
                    >
                      + Record Scenario
                    </button>
                    {advanceHistory.length > 0 && (
                      <button
                        type="button"
                        data-testid="capital-advance-history-toggle"
                        onClick={() => setHistoryOpen((o) => !o)}
                        className="text-[10px] font-mono uppercase tracking-widest text-slate-400 hover:text-violet-300 transition-colors"
                      >
                        {historyOpen ? "▲ Hide" : "▼ Show"} history ({advanceHistory.length})
                      </button>
                    )}
                  </div>

                  {/* Collapsible history table */}
                  {historyOpen && advanceHistory.length > 0 && (() => {
                    const minRepayment = Math.min(...advanceHistory.map((e) => e.totalRepayment));
                    const maxRepayment = Math.max(...advanceHistory.map((e) => e.totalRepayment));
                    return (
                      <div
                        className="mt-3 rounded-xl overflow-hidden"
                        style={{ border: "1px solid rgba(139,92,246,0.18)" }}
                        data-testid="capital-advance-history-table"
                      >
                        {/* Table header */}
                        <div
                          className="grid grid-cols-12 px-4 py-2 text-[9px] font-mono uppercase tracking-widest text-slate-500"
                          style={{ background: "rgba(139,92,246,0.08)", borderBottom: "1px solid rgba(139,92,246,0.12)" }}
                        >
                          <div className="col-span-3">Advance</div>
                          <div className="col-span-2">Factor</div>
                          <div className="col-span-3">Total Repay</div>
                          <div className="col-span-2">Daily Remit</div>
                          <div className="col-span-2 text-right">PDF / Time</div>
                        </div>
                        {/* Rows */}
                        <div className="divide-y" style={{ borderColor: "rgba(139,92,246,0.08)" }}>
                          {advanceHistory.map((e) => {
                            const isCheapest = advanceHistory.length > 1 && e.totalRepayment === minRepayment;
                            const isPriciest = advanceHistory.length > 1 && e.totalRepayment === maxRepayment;
                            return (
                              <div
                                key={e.id}
                                className="grid grid-cols-12 px-4 py-2.5 items-center hover:bg-white/[0.015] transition-colors"
                                data-testid={`history-row-${e.id}`}
                              >
                                <div
                                  className="col-span-3 text-xs font-mono font-bold text-white cursor-pointer"
                                  onClick={() => { setCapitalAdvance(e.advance); setFactorRate(e.factorRate); }}
                                  title="Click to restore this scenario"
                                >
                                  ${e.advance.toLocaleString()}
                                </div>
                                <div className="col-span-2 text-xs font-mono text-violet-300">
                                  {e.factorRate.toFixed(2)}×
                                </div>
                                <div className="col-span-3">
                                  <span
                                    className="text-xs font-mono font-bold"
                                    style={{ color: isCheapest ? "#34d399" : isPriciest ? "#f87171" : "#e2e8f0" }}
                                  >
                                    ${e.totalRepayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                  {isCheapest && <span className="ml-1 text-[8px] font-mono text-emerald-400 uppercase">best</span>}
                                  {isPriciest && <span className="ml-1 text-[8px] font-mono text-red-400 uppercase">most</span>}
                                </div>
                                <div className="col-span-2 text-[10px] font-mono text-emerald-300">
                                  ${e.dailyRemittance.toFixed(2)}
                                </div>
                                <div className="col-span-2 flex items-center justify-end gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => pinScenario(e)}
                                    data-testid={`history-pin-${e.id}`}
                                    className="flex items-center gap-1 px-2 py-0.5 rounded transition-all hover:brightness-110 active:scale-95"
                                    style={
                                      pinnedScenarios.some((p) => p.id === e.id)
                                        ? { background: "rgba(16,185,129,0.18)", border: "1px solid rgba(16,185,129,0.4)", color: "#6ee7b7" }
                                        : { background: "rgba(100,116,139,0.1)", border: "1px solid rgba(100,116,139,0.25)", color: "#94a3b8" }
                                    }
                                    title={pinnedScenarios.some((p) => p.id === e.id) ? "Unpin from compare" : "Pin to compare"}
                                  >
                                    <span className="text-[8px] font-mono uppercase">{pinnedScenarios.some((p) => p.id === e.id) ? "★" : "☆"}</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => downloadTermSheet(e)}
                                    data-testid={`history-pdf-${e.id}`}
                                    className="flex items-center gap-1 px-2 py-0.5 rounded transition-all hover:brightness-110 active:scale-95"
                                    style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)", color: "#c4b5fd" }}
                                    title="Download term sheet PDF"
                                  >
                                    <Download size={9} />
                                    <span className="text-[8px] font-mono uppercase">PDF</span>
                                  </button>
                                  <span className="text-[9px] font-mono text-slate-500">{e.ts}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div
                          className="px-4 py-2 text-[9px] font-mono text-slate-600 text-center"
                          style={{ background: "rgba(139,92,246,0.04)", borderTop: "1px solid rgba(139,92,246,0.08)" }}
                        >
                          Click any row to restore scenario · max 12 entries · green = cheapest · red = most expensive
                        </div>
                      </div>
                    );
                  })()}

                  {/* Send to Accountant panel */}
                  <div
                    className="mt-3 p-3 rounded-xl"
                    style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.16)" }}
                    data-testid="send-to-accountant-panel"
                  >
                    <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mb-2">
                      Email Term Sheet to Accountant
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={accountantEmail}
                        onChange={(e) => setAccountantEmail(e.target.value)}
                        placeholder="accountant@firm.com"
                        data-testid="accountant-email-input"
                        className="flex-1 px-3 py-1.5 rounded-lg text-xs font-mono text-white placeholder-slate-600 outline-none transition-all focus:ring-1"
                        style={{
                          background: "rgba(15,10,40,0.40)",
                          border: "1px solid rgba(139,92,246,0.25)",
                          focusRing: "rgba(139,92,246,0.5)",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const latest = advanceHistory[0];
                          if (latest) sendTermSheetEmail(latest);
                          else toast.error("Record a scenario first");
                        }}
                        disabled={sendingSheet || !accountantEmail}
                        data-testid="send-to-accountant-btn"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest transition-all hover:brightness-110 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: "rgba(139,92,246,0.2)", border: "1px solid rgba(139,92,246,0.4)", color: "#c4b5fd" }}
                      >
                        <Mail size={11} />
                        {sendingSheet ? "Sending…" : "Send to Accountant"}
                      </button>
                    </div>
                    {sheetSentId && (
                      <div className="mt-1.5 text-[10px] font-mono text-emerald-400 flex items-center gap-1">
                        <Check size={9} /> Term sheet sent
                      </div>
                    )}
                  </div>

                  {/* Multi-Scenario Compare View */}
                  {pinnedScenarios.length > 0 && (
                    <div
                      className="mt-4 rounded-xl overflow-hidden"
                      style={{ border: "1px solid rgba(139,92,246,0.25)" }}
                      data-testid="compare-panel"
                    >
                      <div
                        className="flex items-center justify-between px-4 py-2.5"
                        style={{ background: "rgba(139,92,246,0.1)", borderBottom: "1px solid rgba(139,92,246,0.15)" }}
                      >
                        <span className="text-[10px] font-mono uppercase tracking-widest text-violet-300">
                          Scenario Compare · {pinnedScenarios.length}/3 pinned
                        </span>
                        <div className="flex items-center gap-3">
                          {pinnedScenarios.length >= 1 && (
                            <button
                              type="button"
                              onClick={downloadComparePDF}
                              data-testid="compare-pdf-btn"
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[9px] font-mono uppercase tracking-widest transition-all hover:brightness-110 active:scale-95"
                              style={{ background: "rgba(139,92,246,0.2)", border: "1px solid rgba(139,92,246,0.4)", color: "#c4b5fd" }}
                            >
                              <Download size={10} />
                              Compare PDF
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => setPinnedScenarios([])}
                            className="text-[9px] font-mono text-slate-500 hover:text-slate-300 uppercase tracking-widest"
                            data-testid="compare-clear-btn"
                          >
                            Clear all
                          </button>
                        </div>
                      </div>
                      <div className={`grid gap-px ${pinnedScenarios.length === 1 ? "grid-cols-1" : pinnedScenarios.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}
                        style={{ background: "rgba(139,92,246,0.08)" }}>
                        {(() => {
                          const minRep = Math.min(...pinnedScenarios.map((e) => e.totalRepayment));
                          return pinnedScenarios.map((e) => {
                            const isBest = pinnedScenarios.length > 1 && e.totalRepayment === minRep;
                            return (
                              <div
                                key={e.id}
                                className="p-4"
                                style={{
                                  background: isBest ? "rgba(16,185,129,0.06)" : "rgba(8,4,24,0.40)",
                                  border: isBest ? "1px solid rgba(16,185,129,0.25)" : "none",
                                }}
                                data-testid={`compare-scenario-${e.id}`}
                              >
                                {isBest && (
                                  <div className="text-[8px] font-mono uppercase tracking-widest text-emerald-400 mb-2">
                                    ✓ Best Terms
                                  </div>
                                )}
                                <div className="text-xl font-mono font-bold text-white mb-1">
                                  ${e.advance.toLocaleString()}
                                </div>
                                <div className="text-[10px] text-violet-300 font-mono mb-3">{e.factorRate.toFixed(2)}× factor</div>
                                <div className="space-y-1.5">
                                  <div className="flex justify-between">
                                    <span className="text-[10px] text-slate-500 font-mono">Total repay</span>
                                    <span className={`text-[11px] font-mono font-bold ${isBest ? "text-emerald-300" : "text-white"}`}>
                                      ${e.totalRepayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-[10px] text-slate-500 font-mono">Daily remit</span>
                                    <span className="text-[11px] font-mono text-slate-300">${e.dailyRemittance.toFixed(2)}</span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => pinScenario(e)}
                                  className="mt-3 text-[9px] font-mono uppercase tracking-widest text-slate-500 hover:text-red-400 transition-colors"
                                  data-testid={`compare-unpin-${e.id}`}
                                >
                                  Unpin
                                </button>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Premium Tax Shield — Solo 401(k) Retirement Runway Matrix */}
        <div className="mb-6" id="retirement-runway-node" data-testid="retirement-runway-row">
          <PremiumShieldGate
            label="Solo 401(k) · Retirement Runway"
            tagline="Simulate pre-tax retirement sweeps against your net 1099 income and watch your active IRS liability collapse toward zero in real time."
          >
            <RetirementRunwayConsole />
          </PremiumShieldGate>
        </div>

        {/* Premium Tax Shield — Backdoor Roth Matrix */}
        <div className="mb-6" id="backdoor-roth-node" data-testid="backdoor-roth-row">
          <PremiumShieldGate
            label="Backdoor Roth Matrix · § 408A(c)(3)"
            tagline="Auto-checks your income against Roth phase-outs and lays out the exact CPA-executed non-deductible-IRA → Roth conversion steps."
          >
            <BackdoorRothMatrix />
          </PremiumShieldGate>
        </div>

        {/* All-tier · Mileage Deduction Odometer Card */}
        <div className="mb-6" id="mileage-odometer-node" data-testid="mileage-odometer-row">
          <MileageDeductionOdometer />
        </div>

        {/* Quarterly Tax Vault — Premium Shield only */}
        <div className="mb-6" id="quarterly-tax-vault-row" data-testid="quarterly-tax-vault-row">
          <PremiumShieldGate
            label="Quarterly Tax Vault"
            tagline="Auto-allocates your estimated quarterly payments so April never surprises you. Live drift alerts + IRS 1040-ES sync."
          >
            <div className="rounded-2xl p-6 glass" data-testid="quarterly-tax-vault-live">
              <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-purple-300 mb-2">
                Quarterly Tax Vault · Auto-Reserving
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                <div>
                  <div className="text-slate-400 text-xs uppercase tracking-widest">Q1 Reserved</div>
                  <div className="text-white text-2xl font-bold font-mono">$2,340</div>
                </div>
                <div>
                  <div className="text-slate-400 text-xs uppercase tracking-widest">Q2 Projected</div>
                  <div className="text-white text-2xl font-bold font-mono">$2,610</div>
                </div>
                <div>
                  <div className="text-slate-400 text-xs uppercase tracking-widest">Next Due</div>
                  <div className="text-emerald-300 text-2xl font-bold font-mono">Jun 15</div>
                </div>
              </div>
              <div className="text-slate-400 text-sm mt-3">
                On track · zero underpayment penalty risk this year.
              </div>
            </div>
          </PremiumShieldGate>
        </div>

        <WriteOffPredictor stats={stats} receipts={recent} user={user} />

        <PredictiveInsightsTray
          stats={stats}
          recent={recent}
          leaks={leaks}
          alias={alias}
          onCta={(to) => navigate(to)}
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 stagger">
          <Stat label="This Month" value={stats?.this_month || 0} sub={`across ${stats?.receipt_count || 0} receipts`} icon={Calendar} accent loading={!stats} />
          <Stat label="Total Spent" value={stats?.total_spent || 0} sub="all time" icon={TrendingUp} loading={!stats} />
          <Stat label="Tax Deductible" value={stats?.deductible || 0} sub="flagged for write-off" icon={ShieldCheck} loading={!stats} />
          <Stat label="Sales Tax Paid" value={stats?.tax_paid || 0} sub="year-to-date" icon={ReceiptIcon} loading={!stats} />
        </div>

        {/* Email forwarding panel */}
        <div
          className="glass rounded-2xl p-7 mb-8 relative overflow-hidden fade-up"
          data-testid="dash-email-panel"
        >
          <div
            className="absolute -top-24 -right-24 w-72 h-72 rounded-full pointer-events-none opacity-60"
            style={{
              background: "radial-gradient(circle, rgba(16,185,129,0.28), transparent 70%)",
            }}
          />
          <div className="relative grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400 mb-2">
                <Mail size={11} /> Email Ingestion
              </div>
              <h2 className="text-xl font-bold text-white tracking-tight leading-snug">
                Forward any invoice.<br />
                <span className="text-emerald-400">We do the rest.</span>
              </h2>
              <div className="mt-5 flex flex-col sm:flex-row gap-2">
                <div
                  className="flex-1 px-4 h-12 rounded-lg bg-slate-950/70 border border-emerald-500/25 flex items-center font-mono text-sm text-emerald-300 truncate"
                  data-testid="dash-forwarding-alias"
                >
                  {alias}
                </div>
                <button
                  onClick={copyAlias}
                  data-testid="dash-copy-email-btn"
                  className="btn-neon h-12 px-5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold flex items-center gap-2 shrink-0"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? "Copied" : "Copy Email"}
                </button>
              </div>
              <button
                onClick={sendDigest}
                disabled={sendingDigest}
                data-testid="dash-send-digest-btn"
                className="mt-3 text-xs font-mono text-emerald-300/80 hover:text-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 tracking-wide"
              >
                <Send size={11} /> {sendingDigest ? "Preparing digest…" : "Send me a weekly digest preview"}
              </button>
              {/* Digest Preview Panel toggle */}
              <button
                type="button"
                onClick={loadDigestPreview}
                disabled={digestPreviewLoading}
                data-testid="digest-preview-toggle"
                className="mt-2 text-xs font-mono text-violet-300/80 hover:text-violet-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5 tracking-wide"
              >
                <Eye size={11} />
                {digestPreviewLoading ? "Loading preview…" : digestPreviewOpen ? "▲ Hide digest preview" : "Preview digest email"}
              </button>

              {/* Inline digest HTML preview */}
              {digestPreviewOpen && (
                <div
                  className="mt-4 rounded-xl overflow-hidden"
                  style={{
                    border: "1px solid rgba(139,92,246,0.3)",
                    boxShadow: "0 0 24px -8px rgba(139,92,246,0.25)",
                  }}
                  data-testid="digest-preview-panel"
                >
                  <div
                    className="flex items-center justify-between px-4 py-2.5"
                    style={{ background: "rgba(139,92,246,0.12)", borderBottom: "1px solid rgba(139,92,246,0.2)" }}
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={13} className="text-violet-300" />
                      <span className="text-[10px] font-mono uppercase tracking-widest text-violet-300">
                        Weekly Digest Preview — read-only · no email sent
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={11} className="text-slate-400 flex-shrink-0" />
                      <select
                        value={digestInterval}
                        onChange={(e) => updateDigestInterval(e.target.value)}
                        disabled={settingInterval}
                        data-testid="digest-interval-select"
                        className="text-[10px] font-mono rounded-lg px-2 py-1 outline-none cursor-pointer transition-all disabled:opacity-50"
                        style={{
                          background: "rgba(15,10,40,0.40)",
                          border: "1px solid rgba(139,92,246,0.35)",
                          color: "#c4b5fd",
                        }}
                      >
                        <option value={168}>Weekly (168 Hours)</option>
                        <option value={336}>Bi-Weekly (336 Hours)</option>
                        <option value={720}>Monthly (720 Hours)</option>
                      </select>
                      <button
                        type="button"
                        onClick={() => setDigestPreviewOpen(false)}
                        className="text-slate-500 hover:text-slate-300 transition-colors"
                        data-testid="digest-preview-close"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                  {digestPreviewLoading ? (
                    <div
                      className="flex items-center justify-center py-12 text-slate-400 text-xs font-mono tracking-widest"
                      style={{ background: "rgba(8,4,24,0.40)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}
                    >
                      COMPOSING DIGEST…
                    </div>
                  ) : (
                    <iframe
                      title="digest-preview"
                      data-testid="digest-preview-iframe"
                      srcDoc={digestPreviewHtml || ""}
                      className="w-full border-0"
                      style={{ height: "620px", background: "#fff" }}
                      sandbox="allow-same-origin"
                    />
                  )}
                </div>
              )}
            </div>
            <div className="lg:col-span-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-400 mb-3 flex items-center gap-2">
                <Send size={11} /> 3 steps · zero cost
              </div>
              <ol className="grid sm:grid-cols-3 gap-3">
                {[
                  { n: "1", t: "Open the invoice", d: "Amazon, Walmart or DoorDash order email." },
                  { n: "2", t: "Hit Forward", d: "Send it to your address above." },
                  { n: "3", t: "Done", d: "It appears in your vault in seconds." },
                ].map((s) => (
                  <li
                    key={s.n}
                    data-testid={`dash-step-${s.n}`}
                    className="rounded-xl p-4 bg-white/[0.03] border border-white/5"
                  >
                    <div className="w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 flex items-center justify-center font-mono font-bold text-sm mb-2">
                      {s.n}
                    </div>
                    <div className="font-semibold text-white text-sm">{s.t}</div>
                    <div className="text-slate-400 text-xs mt-1 leading-relaxed">{s.d}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Receipts — wrapped in the Perimeter Shield Lighting frame */}
          {(() => {
            // Threshold logic — unverified state fires when >30% of recent receipts
            // are missing categorization OR are high-value round-numbers (fraud pattern).
            const flagged = (recent || []).filter((r) => {
              const total = Number(r.total || 0);
              return !r.category || (total >= 500 && total % 100 === 0);
            });
            const unverifiedRatio = recent.length > 0 ? flagged.length / recent.length : 0;
            const unverified = unverifiedRatio > 0.3;
            return (
              <PerimeterShieldWrapper
                unverified={unverified}
                flaggedCount={flagged.length}
                firstFlaggedId={flagged[0]?.id}
                navigate={navigate}
              >
                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-semibold text-white tracking-tight">Recent Receipts</h2>
                      <span
                        data-testid="perimeter-shield-badge"
                        className="text-[9px] font-mono uppercase tracking-[0.2em] px-2 py-1 rounded-full"
                        style={{
                          background: unverified ? "rgba(245,158,11,0.12)" : "rgba(16,185,129,0.12)",
                          border: `1px solid ${unverified ? "#F59E0B" : "#10B981"}`,
                          color: unverified ? "#F59E0B" : "#10B981",
                        }}
                      >
                        {unverified ? "◆ Unverified · Review" : "● Shield Active"}
                      </span>
                    </div>
                    <button
                      onClick={() => navigate("/receipts")}
                      className="text-emerald-400 text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
                      data-testid="dash-view-all-receipts"
                    >
                      View all <ArrowRight size={14} />
                    </button>
                  </div>
                  {recent.length === 0 ? (
                    <div className="text-slate-500 text-sm text-center py-12">
                      No receipts yet. Scan one to get started.
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {recent.map((r) => {
                        // Deterministic per-merchant hue mapping: hash the
                        // merchant string into one of six cosmic palette anchors
                        // so a given merchant always projects the same aura.
                        const merchantKey = String(r.merchant || r.category || "").toLowerCase();
                        let h = 0;
                        for (let i = 0; i < merchantKey.length; i++) h = (h * 31 + merchantKey.charCodeAt(i)) | 0;
                        const PALETTE = [
                          "rgba(34,211,238,0.55)",   // neon cyan
                          "rgba(16,185,129,0.5)",    // deep emerald
                          "rgba(250,204,21,0.5)",    // electric gold
                          "rgba(168,85,247,0.5)",    // amethyst
                          "rgba(244,63,94,0.5)",     // rose
                          "rgba(59,130,246,0.5)",    // sapphire
                        ];
                        const brandGlow = PALETTE[Math.abs(h) % PALETTE.length];
                        return (
                        <button
                          key={r.id}
                          onClick={() => navigate(`/receipts/${r.id}`)}
                          className="merchant-brand-row w-full flex items-center gap-4 py-3.5 text-left px-2 rounded-lg"
                          style={{ "--brand-glow": brandGlow }}
                          data-testid={`recent-receipt-${r.id}`}
                        >
                          <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                            <ReceiptIcon size={18} className="text-slate-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-white truncate">{r.merchant}</div>
                            <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                              <span className="font-mono">{r.date}</span>
                              <span>·</span>
                              <span>{CAT_LABELS[r.category] || r.category}</span>
                              {r.tax_deductible && (
                                <>
                                  <span>·</span>
                                  <span className="text-emerald-400">Deductible</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-mono font-bold text-white">${Number(r.total).toFixed(2)}</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mt-0.5">
                              {r.currency}
                            </div>
                          </div>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </PerimeterShieldWrapper>
            );
          })()}

          {/* Category breakdown */}
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white tracking-tight">By Category</h2>
              <Sparkles size={16} className="text-emerald-400" />
            </div>
            {topCategories.length === 0 ? (
              <div className="text-slate-500 text-sm text-center py-8">No data yet.</div>
            ) : (
              <div className="space-y-4">
                {topCategories.map(([cat, val]) => (
                  <div key={cat}>
                    <div className="flex justify-between items-baseline mb-1.5">
                      <span className="text-sm text-slate-200">
                        {CAT_LABELS[cat] || cat}
                      </span>
                      <span className="font-mono text-sm text-emerald-300 font-semibold">
                        ${val.toFixed(2)}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-700"
                        style={{ width: `${(val / maxCatVal) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Subscription Leaks */}
        {leaks.length > 0 && (
          <div className="mt-6 glass rounded-2xl p-6" data-testid="leaks-panel">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-semibold text-white tracking-tight flex items-center gap-2">
                  <Droplet size={16} className="text-amber-400" />
                  Subscription Leak Detector
                </h2>
                <p className="text-slate-400 text-sm mt-1">
                  Recurring merchants tracked across the last 90 days. Flatlines
                  usually mean a paid-but-unused service.
                </p>
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                {leaks.filter((l) => l.leak_status === "flatlined").length} flagged · {leaks.length} tracked
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {leaks.slice(0, 6).map((l) => (
                <div
                  key={l.merchant}
                  data-testid={`leak-${l.merchant.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`rounded-xl p-4 border ${
                    l.leak_status === "flatlined"
                      ? "bg-amber-500/10 border-amber-500/30"
                      : "bg-white/[0.03] border-white/5"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-white truncate">{l.merchant}</div>
                    <span
                      className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded ${
                        l.leak_status === "flatlined"
                          ? "bg-amber-500/25 text-amber-200"
                          : "bg-emerald-500/15 text-emerald-300"
                      }`}
                    >
                      {l.leak_status === "flatlined" ? "LEAK" : "OK"}
                    </span>
                  </div>
                  <div className="mt-3 flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">Est. annual</span>
                    <span className="font-mono font-bold text-emerald-300">
                      ${l.annualized_spend.toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-slate-400 font-mono">
                    Every ~{l.avg_interval_days}d · last {l.days_since_last}d ago
                  </div>
                  {l.leak_status === "flatlined" && l.days_overdue > 0 && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-amber-300 font-mono">
                      {l.days_overdue}d overdue — verify or cancel
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {user?.space_age_deck_enabled && <CommodityTicker />}
    </DashboardLayout>
  );
}
